import { z } from "zod";

// ProseMirror document node — validated loosely (type + content array only).
// Deeper structural validation is brittle and unnecessary.
export const DocumentContentSchema = z
  .object({
    type: z.literal("doc"),
    content: z.array(z.any()).optional(),
  })
  .passthrough();

export type TDocumentContent = z.infer<typeof DocumentContentSchema>;

export const SignaturePositionSchema = z.enum([
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "none",
]);
export type TSignaturePosition = z.infer<typeof SignaturePositionSchema>;

export const DocumentTemplateSchema = z.object({
  id: z.string().min(1),
  doctorId: z.string().min(1),
  title: z.string().trim().min(1).max(120),
  content: DocumentContentSchema,
  createdAt: z.number().int().positive().finite(),
  updatedAt: z.number().int().positive().finite(),
});

export type TDocumentTemplate = z.infer<typeof DocumentTemplateSchema>;

export const NewDocumentTemplateSchema = DocumentTemplateSchema.omit({
  id: true,
  doctorId: true,
  createdAt: true,
  updatedAt: true,
});
export type TNewDocumentTemplate = z.infer<typeof NewDocumentTemplateSchema>;

// Request payload for the renderDocumentToPdf callable
export const RenderDocumentRequestSchema = z.object({
  requestId: z.string().min(1).max(128),
  templateId: z.string().min(1),
  content: DocumentContentSchema,
  title: z.string().trim().min(1).max(120),
  signaturePosition: SignaturePositionSchema,
  // No approveMessageByDoctor: generating a document from a template does not
  // approve the request, so there is no approval note to carry. The doctor
  // writes it in the אישור dialog, which is what actually approves.
});
export type TRenderDocumentRequest = z.infer<typeof RenderDocumentRequestSchema>;

export const RenderDocumentResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    pdfUrl: z.string().url(),
    pdfPath: z
      .string()
      .regex(
        /^requests\/[a-zA-Z0-9_\-]+\/documents\/[a-zA-Z0-9_\-]+\.pdf$/,
        { message: "pdfPath must match requests/{id}/documents/{id}.pdf" }
      ),
    size: z.number().positive(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
  }),
]);
export type TRenderDocumentResponse = z.infer<typeof RenderDocumentResponseSchema>;
